import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CATEGORY_LABEL } from '@/modules/sales-research/lib';
import { useSalesOutlets } from '@/modules/sales-research/api';
import { useCrmAction } from './api';
import { Modal, Field, TextInput, Textarea, Select } from './widgets';

const ROLES = ['admin', 'hr', 'facilities', 'office_manager', 'people_ops', 'procurement', 'founder', 'other'];

/** Manual lead entry — a sales exec adds a company + primary contact by hand. */
export function NewLeadModal({ open, onClose, managerNames = [] }: { open: boolean; onClose: () => void; managerNames?: string[] }) {
  const act = useCrmAction();
  const outletsQ = useSalesOutlets();
  const [f, setF] = useState<any>({ category: 'other', role_category: 'admin', visited: true });
  const set = (k: string, v: any) => setF((s: any) => ({ ...s, [k]: v }));
  const outlets = outletsQ.data ?? [];

  async function save() {
    const res: any = await act.mutateAsync({
      action: 'create_company',
      name: f.name, category: f.category, city: f.city || null, website: f.website || null,
      notes: f.notes || null, outlet_id: f.outlet_id || null, assigned_name: f.assigned_name || null,
      contact: (f.contact_name || f.contact_email)
        ? { full_name: f.contact_name || null, title: f.contact_title || null, role_category: f.role_category, email: f.contact_email || null, phone: f.contact_phone || null }
        : undefined,
    });
    // Log a site visit on the timeline when the rep was physically there.
    const companyId = res?.company?.id;
    if (f.visited && companyId) {
      await act.mutateAsync({
        action: 'log_activity', company_id: companyId, kind: 'visit', direction: 'outbound',
        body: f.notes || 'Site visit logged', occurred_at: new Date().toISOString(),
      });
    }
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add company / log visit">
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Log a company a rep visited or found in the field — it drops straight into the pipeline.
        </p>
        <Field label="Company name *"><TextInput value={f.name ?? ''} onChange={(e) => set('name', e.target.value)} placeholder="Acme Corp" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={f.category} onChange={(v) => set('category', v)} className="w-full"
              options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="City"><TextInput value={f.city ?? ''} onChange={(e) => set('city', e.target.value)} placeholder="Mumbai" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Website"><TextInput value={f.website ?? ''} onChange={(e) => set('website', e.target.value)} placeholder="acme.com" /></Field>
          <Field label="Owner outlet">
            <Select value={f.outlet_id ?? ''} onChange={(v) => set('outlet_id', v)} placeholder="—" className="w-full"
              options={outlets.map((o) => ({ value: o.outlet_id, label: o.name }))} />
          </Field>
        </div>
        <Field label="Sales manager">
          <input list="mgr-names" value={f.assigned_name ?? ''} onChange={(e) => set('assigned_name', e.target.value)}
            placeholder="Who owns this lead?"
            className="h-9 w-full rounded-md border border-border/60 bg-transparent px-2.5 text-sm outline-none focus:border-[#51BFAA]" />
          <datalist id="mgr-names">{managerNames.map((n) => <option key={n} value={n} />)}</datalist>
        </Field>

        <div className="rounded-lg border border-border/60 p-3">
          <p className="mb-2 text-xs font-semibold text-muted-foreground">Primary contact (optional)</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><TextInput value={f.contact_name ?? ''} onChange={(e) => set('contact_name', e.target.value)} /></Field>
            <Field label="Title"><TextInput value={f.contact_title ?? ''} onChange={(e) => set('contact_title', e.target.value)} /></Field>
            <Field label="Role">
              <Select value={f.role_category} onChange={(v) => set('role_category', v)} className="w-full"
                options={ROLES.map((r) => ({ value: r, label: r.replace('_', ' ') }))} />
            </Field>
            <Field label="Phone"><TextInput value={f.contact_phone ?? ''} onChange={(e) => set('contact_phone', e.target.value)} /></Field>
          </div>
          <div className="mt-2"><Field label="Email"><TextInput type="email" value={f.contact_email ?? ''} onChange={(e) => set('contact_email', e.target.value)} placeholder="name@acme.com" /></Field></div>
        </div>

        <Field label="Notes"><Textarea rows={2} value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} placeholder="What happened on the visit — interest, headcount, next step…" /></Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.visited !== false} onChange={(e) => set('visited', e.target.checked)} />
          Log this as a site visit (adds a visit to the timeline)
        </label>

        {act.error && <p className="text-xs text-[#B15599]">{String(act.error)}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={act.isPending || !f.name} onClick={save}>
            {act.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {f.visited !== false ? 'Add + log visit' : 'Add company'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
